import { FC, useMemo } from 'react'
import * as styles from 'components/features/pfm/AccountsSummaryModule/AccountsSummaryModule.styl'
import { useStoreSelector, useShallowSelector } from 'helpers/reactReduxHelpers'
import { selectedRelationSelector, createAvgsByTypeSelector } from 'selectors/peergroupSelectors'
import { TotalTyped } from 'interfaces/rank'
import { TimeframeTyped } from 'interfaces/global'
import { GuardsParam } from 'interfaces/guards'
import { createParallelGuard, connectLoadable } from 'helpers/loadableHelpers'
import { memberInfoGuard, selectedPeergroupTotalsGuard, peergroupsGuard } from 'guards/globalGuards'
import { memberAllAccountsGuard, accountRatesGuard } from 'guards/accountGuards'
import { formatTableCurrency } from 'helpers/tableHelpers'
import { BarChart } from 'components/features/pfm/BarChart/BarChart'
import { getMax } from 'helpers/formatHelpers'
import { CompetitiveAccounts } from 'components/features/pfm/CompetitiveAccounts/CompetitiveAccounts'
import { categoryInterestsSelector } from 'selectors/accountSelectors'
import { CategorizedPageTyped } from 'interfaces/pages'
import { ALL_CATEGORY_ID } from 'constants/meta'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import Button from 'components/features/pfm/Button/Button'
import ChartHeader from 'components/atoms/charts/ChartHeader'
import { withComparisonSelector } from 'components/features/pageFilters/selectors'

type AccountsSummaryModuleProps =
  & TimeframeTyped
  & TotalTyped
  & CategorizedPageTyped
  & {
  }

export const AccountsSummary: FC<AccountsSummaryModuleProps> = (props) => {
  const { type } = props

  const withComparison = useStoreSelector(withComparisonSelector)

  const otherTitle = useStoreSelector((state) => {
    const selectedRelation = selectedRelationSelector(state)
    return selectedRelation ? selectedRelation.name : ''
  })

  const avgsByTypeSelector = useMemo(createAvgsByTypeSelector, [])
  const avgs = useShallowSelector((state) => avgsByTypeSelector(state, props))

  const categoryInterests = useStoreSelector((state) => categoryInterestsSelector(state, props))

  if (!avgs || !categoryInterests) {
    return null
  }

  return (
    <div className={ styles.accountsSummaryModule }>
      <div className={ styles.legend }>
        <ChartHeader
          className={ styles.header }
          member={{
            label: 'You',
            value: formatTableCurrency(avgs.memberValue, 'dollar'),
          }}
          peers={{
            label: 'Your Peers',
            value: formatTableCurrency(avgs.peerValue, 'dollar'),
          }}
          others={{
            label: otherTitle,
            value: formatTableCurrency(avgs.otherValue, 'dollar'),
          }}
          withComparison={ withComparison }
        />
      </div>

      { withComparison && (
        <div className={ styles.chart }>
          <BarChart
            { ...avgs }
            max={ getMax([avgs.memberValue, avgs.peerValue, avgs.otherValue]) }
          />
        </div>
      )}
      <div className={ styles.competitive }>
        <CompetitiveAccounts
          { ...categoryInterests.byKey[ALL_CATEGORY_ID] }
          type={ type }
        />
      </div>

      <div className={ styles.actions }>
        <NavigationLink to='settings_accounts'>
          <Button
            style='textOnly'
            size='mini'
          >
            MANAGE ACCOUNTS
          </Button>
        </NavigationLink>

        <NavigationLink to={ type === 'debts' ? 'debts_rates' : 'assets_rates' }>
          <Button
            style='hollow'
            size='mini'
          >
            SEE ALL RATES
          </Button>
        </NavigationLink>
      </div>
    </div>
  )
}

const createGuards: GuardsParam<AccountsSummaryModuleProps> = () => [
  createParallelGuard(
    memberInfoGuard,
    peergroupsGuard,
  ),
  createParallelGuard(
    memberAllAccountsGuard,
    accountRatesGuard,
    selectedPeergroupTotalsGuard,
  ),
]

export const AccountsSummaryModule = connectLoadable<
  {}, {}, AccountsSummaryModuleProps
>(createGuards, () => null)(() => () => ({}), () => ({}))(AccountsSummary)

